import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { httpErrorInterceptor } from '@core/interceptors/http-error-interceptor';
import { authenticationInterceptor } from '@auth/interceptors/authentication-interceptor';
import { theme } from './theme';
import { environment } from '@environment';

/**
 * Sets inputmode="none" on hiffen input inside p-multiselect.
 * This prevents the virtual keyboard from appearing on mobile when interacting with multiselects.
 *
 * Uses a single document-level MutationObserver so it applies globally with zero per-component
 * wiring. The observer runs outside Angular's change detection (no zone involvement).
 */
function initMultiselectInputmode(): void {
  const selector = 'p-multiselect input[role="combobox"]';

  const applyInputmode = (root: ParentNode = document) => {
    root.querySelectorAll<HTMLInputElement>(selector).forEach((input) => {
      input.inputMode = 'none';
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof Element) {
          if (node.matches(selector)) {
            (node as HTMLInputElement).inputMode = 'none';
          }

          applyInputmode(node);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    provideAppInitializer(initMultiselectInputmode),
    providePrimeNG({
      ripple: true,
      theme,
    }),
    MessageService,
    provideHttpClient(withInterceptors([authenticationInterceptor, httpErrorInterceptor])),
  ],
};
